import {
  MainMetaMap,
  MainMetaMapSubTopic,
  MainMetaMapTopic,
} from "@/types/meta-schema";
import type { Metadata } from "next";
import { getMainMetaMap } from "./dataFetching";
import { parseSubjectUrl, titleToUrl } from "./routing";

// **************************************************************************************** STRUCTURED SNIPPETS
export const reviews: Review[] = [
  {
    name: "A. Petrakov",
    title: "A Godsend",
    review: "I found this before exams, it is a godsend and a miracle.",
    rating: 5,
  },
  {
    name: "J. Don",
    title: "It Has Everything",
    review:
      "This is the most fantastic website I have ever used. It contains everything important needed from a website: Question-banks, videos, notes. You name it, this website has it.",
    rating: 5,
  },
  {
    name: "J. Kan",
    title: "Crucial For My Academics",
    review:
      "I wholeheartedly recommend Tiber Tutor to any IB student seeking a reliable and supportive resource for mastering the intricacies of science subjects. Thank you, Tiber Tutor, for being a crucial partner in my academic journey!",
    rating: 5,
  },
  {
    name: "M. Civi",
    title: "Saved My Daughter",
    review:
      "I bought the program for my daughter, and the improvement in her academic results was incredible, she went from understanding nothing to achieving the best results in her class with minimal effort. It contains some exceptional explanatory videos. If you want to get a good result in the IB, get them or get your school to get the programs, my daughter's teacher, seeing the change in her, bought it at the school!",
    rating: 5,
  },
  {
    name: "A. Dish",
    title: "Incredible",
    review:
      "Honestly the best, it will teach you the entire syllabus in 2 days",
    rating: 5,
  },
  {
    name: "E. Amondi",
    title: "Best Site Out There",
    review:
      "I find the Tiber tutor extremely helpful, I take biology and chemistry. So far, it has the most accessible format for tracking my progress and the notes are tailored to give what I need to know, especially now that my finals are approaching fast, I honestly love it",
    rating: 5,
  },
  {
    name: "J. Miranda",
    title: "Great For Teachers",
    review: "This is truly a great resource for Science teachers.",
    rating: 5,
  },
  {
    name: "T. Celic",
    title: "My Secret To Success",
    review:
      "Since I started using Tiber Tutor, I started learning how to answer questions to get full marks. After using Tiber Tutor's resources for studying, there were almost never questions I did not understand or recognise!",
    rating: 5,
  },
  {
    name: "J. Evans",
    title: "Just Excellent",
    review:
      "Tiber Tutor is an excellent resource. It gives you the opportunity to practice past paper questions and understand why your answer was incorrect if you got it wrong. The videos are great because they give a simple explanation of the content with examples. A great resource to prepare for summative and IB exams.",
    rating: 5,
  },
  {
    name: "F. Stanislawski",
    title: "Perfect For Class",
    review:
      "Great access to exam resources with useful mark schemes, useful in class when practising exam questions.",
    rating: 5,
  },
  {
    name: "J. Don",
    title: "Highly Recommend",
    review:
      "I have been using this website for the past 2 months. Its question banks for both Physics and Chemistry have been very beneficial for studying, and I would highly recommend this website for anyone taking IB.",
    rating: 5,
  },
  {
    name: "A. Zaikin",
    title: "Gives Me 7's!",
    review: "It's soo clear, amazing and helps me get 7's!!!",
    rating: 5,
  },
];

export function getReviewSnippets(subjectName: string) {
  const review = reviews[Math.floor(Math.random() * reviews.length)];
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `IB ${subjectName} Membership`,
    url: `/ib/${titleToUrl(subjectName)}`,
    description: `IB ${{
      subjectName,
    }} videos, notes, flashcards, questions, cramsheets, and definitions all organised by sub-topic.`,
    brand: {
      "@type": "Brand",
      name: "Tiber Tutor",
    },
    review: {
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
        bestRating: "5",
      },
      name: review.review,
      author: {
        "@type": "Person",
        name: review.name,
      },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "623",
    },
  };
}

export function getFaqSnippets() {
  const faqs = [
    {
      question: "What is Tiber Tutor",
      answer:
        "Tiber Tutor is an IB-dedicated revision platform for Biology, Chemistry and Physics that brings everything into one place: animated videos, concise notes, flashcards, cram-sheets, exam-style questions and detailed mark schemes, all organised by topic and sub-topic.",
    },
    {
      question: "What IB subjects and resources does Tiber Tutor cover",
      answer:
        "Tiber Tutor focuses on IB sciences, currently covering IB Biology, IB Chemistry and IB Physics at both SL and HL. For each subject you get professional animated videos, reliable revision notes, thousands of flashcards, exam-style questions with worked solutions, cram-sheets and key definition lists.",
    },
    {
      question: "Are Tiber Tutor resources up-to-date with the IB syllabus",
      answer:
        "Revising from out of date sources is damaging. Our team of experts works tirelessly to update and curate new resources in line with the latest IB syllabus.",
    },
    {
      question:
        "Does Tiber Tutor help with IB science exam questions and past-paper style practice",
      answer:
        "Yes. Our questionbank is built around exam-style IB science questions with clear mark schemes and step-by-step worked solutions. You can drill questions by topic and difficulty, build custom tests, and use them as past-paper style practice for your IB Biology, Chemistry and Physics exams.",
    },
    {
      question:
        "How is Tiber Tutor different from Revision Village, Save My Exams or Revision Dojo",
      answer:
        "Traditional IB revision sites and questionbanks are often focused on either maths or static question sets. Tiber Tutor is built specifically for IB sciences and combines high-quality videos, notes, flashcards, questionbanks, cram-sheets and definitions with custom tests and an integrated progress-tracking dashboard, so you can see exactly which IB topics you have mastered and which you still need to revise.",
    },
    {
      question: "Why Tiber Tutor over free services like Youtube",
      answer:
        "Watching random videos can help with individual concepts, but it is hard to cover the full IB syllabus or track what you have actually learned. Tiber Tutor gives you a structured IB science course with syllabus-aligned videos, notes, questionbanks and flashcards, plus tests and analytics so you can systematically cover every topic and avoid gaps before your exams.",
    },
    {
      question: "Does Tiber Tutor track my IB revision progress",
      answer:
        "Yes. Your work on videos, questions and tests feeds into a visual progress tracker so you can see how much of the IB Biology, Chemistry and Physics syllabus you have covered, which topics are weak, and where to focus next.",
    },
    {
      question: "Can I trial the Tiber Tutor content for free",
      answer:
        "With a free 7-day trial, you can access everything completely free. No payment information is required to start your trial, so you can explore the full IB science platform before deciding.",
    },
    {
      question: "How do I cancel my Tiber Tutor membership",
      answer:
        "You stay in control of your subscription. You can cancel your membership yourself from the profile menu in just a couple of clicks, and your access will run until the end of the current billing period.",
    },
    {
      question: "Can I login on multiple devices",
      answer:
        "To help maintain all our users security, each user can login with two devices at any time. If this limit is reached, devices will be forcibly logged out.",
    },
    {
      question: "What currencies does Tiber Tutor accept",
      answer:
        "We accept USD, GBP and EUR. To ensure everyone can join, we accept nearly all payment providers through Stripe",
    },
    {
      question: "What devices work with Tiber Tutor",
      answer:
        "We have worked hard to ensure every device is compatible with Tiber Tutor. If you find any issues, please let us know.",
    },
    {
      question: "How do I verify my account",
      answer: "You will receive an email with a link. Simply click it.",
    },
  ];
  return {
    "@context": "https://schema.org",
    "@page": "FAQPage",
    mainEntity: faqs.map((faq) => {
      return {
        "@page": "Question",
        name: faq.question,
        acceptedAnswer: { "@page": "Answer", text: faq.answer },
      };
    }),
  };
}

// export function getCourseSnippets(subjectNames: Subject["Name"][]) {
//   return {
//     "@context": "https://schema.org",
//     "@page": "ItemList",
//     itemListElement: subjectNames.map((subjectName, indx) => {
//       return {
//         "@page": "ListItem",
//         position: indx + 1,
//         item: {
//           "@page": "Course",
//           url: `${process.env.NEXT_PUBLIC_FRONTEND_BASE_URL}/ib/${titleToUrl(
//             subjectName,
//           )}`,
//           name: `IB ${capAll(subjectName)} Revision Resources`,
//           description: `Gain access to videos, notes, flashcards, questions, definitions, and cramsheets for the entire IB ${{subjectName}} course.`,
//           provider: {
//             "@page": "Organization",
//             name: "Tiber Tutor",
//             sameAs: process.env.NEXT_PUBLIC_FRONTEND_BASE_URL,
//           },
//           hasCourseInstance: [
//             {
//               "@page": "CourseInstance",
//               courseMode: "Online",
//               courseWorkload: "P730D",
//             },
//           ],
//           offers: [
//             {
//               "@page": "Offer",
//               category: "Paid",
//               priceCurrency: "GBP",
//               price: 91,
//             },
//             {
//               "@page": "Offer",
//               category: "Paid",
//               priceCurrency: "USD",
//               price: 110,
//             },
//             {
//               "@page": "Offer",
//               category: "Paid",
//               priceCurrency: "EUR",
//               price: 104,
//             },
//           ],
//         },
//       };
//     }),
//   };
// }

// **************************************************************************************** OPEN GRAPH
export async function getResourceMetaComponents(
  props: OpenGraphMetaProps,
): Promise<Metadata> {
  const { page } = props;
  const params = "params" in props ? props.params : undefined;
  const mainMetaMap = await getMainMetaMap();
  const subjectName = params ? parseSubjectUrl(params.subject) : undefined;
  const shortSubjectName = subjectName
    ? getShorthandSubject(subjectName)
    : undefined;
  const currentYear = new Date().getFullYear();
  const { topicMeta, subTopicMeta } = getTopicAndSubTopicMetaFromParams(
    mainMetaMap,
    params,
  );
  let keywords =
    subjectName && shortSubjectName
      ? getResourceLevelKeywords(
          page,
          subjectName,
          shortSubjectName,
          topicMeta,
          subTopicMeta,
        )
      : [];
  let url = params ? getResourceLevelUrl(page, params) : "";
  let title =
    params && subjectName
      ? getResourceLevelTitle(
          page,
          subjectName,
          topicMeta?.number,
          subTopicMeta?.label,
        )
      : "";
  let description =
    params && subjectName && shortSubjectName
      ? getResourceLevelDescription(
          page,
          subjectName,
          shortSubjectName,
          topicMeta,
          subTopicMeta,
        )
      : "";
  /*
   **BUILD URL, TITLE AND DESCRIPTION
   */
  switch (page) {
    // ****************************************************** MAIN PAGES
    case "home":
      url = `/`;
      title = `IB Science Questionbank`;
      description = `Tiber Tutor is the IB science revision platform with a full IB question bank for Biology, Chemistry and Physics: exam-style questions, animated videos, notes, flashcards, cram sheets, topic tests, mock exams and progress tracking.`;
      keywords = ["Tiber Tutor", "IB science revision", "IB question bank"];
      break;
    case "team":
      url = `/team`;
      title = `Tiber Tutor Team`;
      description = `Meet the Tiber Tutor Team. Our Tiber Tutor members and Tiber Tutor creators are the experts behind our IB Biology, Chemistry and Physics resources, working globally to deliver top-quality revision content.`;
      keywords = [
        "Tiber Tutor Team",
        "Tiber Tutor Members",
        "Tiber Tutor Creators",
      ];
      break;
    case "pricing":
      url = `/pricing`;
      title = "Tiber Tutor Pricing";
      description =
        "Explore our membership plans and unlock a free 7-day trial. Our pricing is transparent and fair. We allow flexible additions of subjects and easy cancellation.";
      keywords = [
        "Tiber Tutor Pricing",
        "Tiber Tutor Memberships",
        "Tiber Tutor Plans",
      ];
      break;
    case "about":
      url = `/about`;
      title = `About Tiber Tutor`;
      description = `About Tiber Tutor: our mission and vision for IB science. Learn why we focus on Biology, Chemistry and Physics and how our videos, notes, questionbanks and tests help you succeed in the IB.`;
      keywords = [
        "Tiber Tutor About",
        "Tiber Tutor Mission",
        "Tiber Tutor Vision",
      ];
      break;
    case "parents":
      url = `/parents`;
      title = `For IB Parents » Tiber Tutor`;
      description = `See why parents and schools trust Tiber Tutor for IB sciences. Understand how our expert-led resources, progress tracking and flexible pricing support your child's IB journey.`;
      keywords = [
        "Tiber Tutor Parents",
        "IB science parents",
        "IB science parent tips",
      ];
      break;
    case "students":
      url = `/students`;
      title = `For IB Students » Tiber Tutor`;
      description = `Everything you need to succeed in IB Biology, Chemistry and Physics, in one place. Explore videos, notes, questions, flashcards and more—then start a free 7-day trial with no payment details required.`;
      keywords = [
        "Tiber Tutor Students",
        "IB student resources",
        "IB science question bank",
      ];
      break;
    case "terms":
      url = `/terms`;
      title = `Terms & Conditions`;
      description = `Tiber Tutor terms and Tiber Tutor conditions: read our full Tiber Tutor agreement before signing in and using our resources or services.`;
      keywords = [
        "Tiber Tutor Terms",
        "Tiber Tutor Conditions",
        "Tiber Tutor Agreement",
      ];
      break;
    case "privacy":
      url = `/privacy`;
      title = `Privacy Policy`;
      description = `Tiber Tutor privacy and data protection. Our Tiber Tutor policy explains how we handle your information; please read before signing in and using our resources.`;
      keywords = [
        "Tiber Tutor Privacy",
        "Tiber Tutor Policy",
        "Tiber Tutor Data Protection",
      ];
      break;
    case "blog":
      url = `/blog`;
      title = `Tiber Tutor Blog`;
      description = `Study tips, exam strategies and IB science insights from Tiber Tutor. Articles for IB Biology, Chemistry and Physics students and teachers.`;
      keywords = ["Tiber Tutor blog", "IB science blog", "IB study tips"];
      break;
    case "blog article": {
      const { blogArticle } = props;
      url = `/blog/${blogArticle.slug}`;
      title = blogArticle.title;
      description =
        blogArticle.metaDescription?.trim() ||
        `Read ${blogArticle.title} on the Tiber Tutor blog—IB Biology, Chemistry and Physics revision and insights.`;
      keywords = blogArticle.keywords;
      break;
    }
    // ****************************************************** SUBJECT RESOURCES
    case "overall subject":
      url = `/ib/${params!.subject}`;
      title = `IB ${subjectName} Resources`;
      description = `Your IB ${subjectName} resources and study guide for SL and HL revision: videos, notes, flashcards, exam-style questions, cram sheets, topic tests and mock exams, organised by topic and sub-topic.`;
      keywords = [
        `IB ${subjectName}`,
        `IB ${subjectName} resources`,
        `SL IB ${subjectName} revision`,
        `HL IB ${subjectName} revision`,
        `IB ${shortSubjectName} study guide`,
      ];
      break;
    case "syllabus":
      url = `/ib/${params!.subject}/syllabus`;
      title = `IB ${subjectName} Syllabus`;
      description = `IB ${subjectName} syllabus for SL and HL. Use our interactive checklist as your IB ${shortSubjectName} progress tracker: tick off topics and subtopics and stay on track for ${currentYear} exams.`;
      keywords = [
        `IB ${subjectName} syllabus`,
        `SL IB ${subjectName} syllabus`,
        `HL IB ${subjectName} syllabus`,
        `IB ${shortSubjectName} checklist`,
        `IB ${shortSubjectName} progress tracker`,
      ];
      break;
    case "test taker":
      url = `/ib/${params!.subject}/test-taker`;
      title = `IB ${subjectName} Custom Test`;
      description = `IB ${subjectName} custom test page for test takers. Sit timed tests from our questionbank and use the test tracker to monitor your IB ${shortSubjectName} test progress topic by topic.`;
      keywords = [
        `IB ${subjectName} custom test`,
        `IB ${shortSubjectName} test taker`,
        `IB ${subjectName} test tracker`,
        `IB ${subjectName} test progress`,
      ];
      break;
    case "test builder":
      url = `/ib/${params!.subject}/test-builder`;
      title = `IB ${subjectName} Test Builder`;
      description = `IB ${subjectName} test builder and exam generator. Create custom tests with our exam maker: choose topics, difficulty and marks to build IB ${shortSubjectName} practice papers from our questionbank.`;
      keywords = [
        `IB ${subjectName} test builder`,
        `IB ${subjectName} test creator`,
        `IB ${shortSubjectName} exam maker`,
        `IB ${subjectName} exam generator`,
      ];
      break;
    case "exam tests":
      url = `/ib/${params!.subject}/exam-tests`;
      title = `IB ${subjectName} Tests`;
      description = `IB ${subjectName} tests and practice tests that mirror real exams. Browse pre-built topic tests and past-exam-style papers to prepare for your IB ${shortSubjectName} exams.`;
      keywords = [
        `IB ${subjectName} tests`,
        `IB ${subjectName} past exams`,
        `IB ${shortSubjectName} practice tests`,
        `IB ${subjectName} real exams`,
      ];
      break;
    case "mock exams":
      url = `/ib/${params!.subject}/mock-exams`;
      title = `IB ${subjectName} Mock Exams`;
      description = `IB ${subjectName} mock exams and practice papers. Full-length past-paper-style exams with real timing and structure so you can practise under exam conditions.`;
      keywords = [
        `IB ${subjectName} mock exams`,
        `IB ${shortSubjectName} past papers`,
        `IB ${subjectName} practice papers`,
      ];
      break;
    // ****************************************************** TOPIC RESOURCES
    // *************** those just with topic
    // all handled by setup params
    case "videos":
      break;
    case "cram sheets":
      break;
    case "topic tests":
      break;
    // *************** those also with subtopics
    // all handled by setup params
    case "definitions":
      break;
    case "flashcards":
      break;
    case "notes":
      break;
    case "questions":
      break;
    // ****************************************************** USEFUL RESOURCES
    case "command terms":
      url = `/ib/${params!.subject}/useful-resources/command-terms`;
      description = `Official IB ${subjectName} command terms and what examiners mean by each. Use this list when practising ${shortSubjectName} questions so your answers match what the mark scheme expects. Covers SL and HL.`;
      keywords = [
        `IB ${subjectName} command terms`,
        `IB ${shortSubjectName} vocab list`,
        `IB ${subjectName} terminology`,
      ];
      break;
    case "exam breakdown":
      url = `/ib/${params!.subject}/useful-resources/exam-breakdown`;
      description = `IB ${subjectName} exam structure and timing: number of questions, marks and time for Paper 1, Paper 2 and Paper 3. Plan your revision and exam technique for IB ${shortSubjectName} SL and HL.`;
      keywords = [
        `IB ${subjectName} exam breakdown`,
        `IB ${subjectName} paper structure`,
        `IB ${shortSubjectName} exam timing`,
        `IB ${shortSubjectName} exam format`,
      ];
      break;
    case "ia criteria":
      url = `/ib/${params!.subject}/useful-resources/internal-assessment/ia-criteria`;
      description = `IB ${subjectName} Internal Assessment criteria and mark scheme. See how examiners mark the IA and what to include for full marks. Essential reading before writing your IB ${shortSubjectName} IA.`;
      keywords = [
        `IB ${subjectName} IA criteria`,
        `IB ${subjectName} Internal Assessment criteria`,
        `IB ${subjectName} Internal Assessment mark scheme`,
        `IB ${shortSubjectName} IA rubric`,
      ];
      break;
    case "ia examples":
      url = `/ib/${params!.subject}/useful-resources/internal-assessment/ia-examples`;
      description = `IB ${subjectName} IA examples and sample Internal Assessments to download. Use them for IA ideas, structure and examiner commentary. Supports IB ${shortSubjectName} SL and HL.`;
      keywords = [
        `IB ${subjectName} IA examples`,
        `IB ${shortSubjectName} Internal Assessment sample`,
        `IB ${subjectName} Internal Assessment download`,
        `IB ${shortSubjectName} IA structure`,
      ];
      break;
    // ****************************************************** RANDOM
    case "login":
      url = "/login";
      title = "Login";
      description =
        "Tiber Tutor Login Page: Login to Tiber Tutor and access your account and our subject resources.";
      keywords = [
        `Tiber Tutor login`,
        `Tiber Tutor login page`,
        `Tiber Tutor sign in`,
      ];
      break;
    case "register":
      url = "/register";
      title = "Register";
      description =
        "Tiber Tutor registration page. Register for full access to our current and future IB Biology, IB Chemistry, and IB Physics content.";
      keywords = [
        `Tiber Tutor register`,
        `Tiber Tutor register page`,
        `Tiber Tutor registration`,
      ];
      break;
  }

  // ****************************************************** RETURN METADATA
  // open graph images are hanled by route params.
  const formattedTitle = capitaliseEachWord(title ?? "Tiber Tutor").replaceAll(
    " Ia ",
    " IA ",
  );
  return {
    title: `${formattedTitle} » Tiber Tutor`,
    description,
    keywords,
    alternates: {
      canonical: url,
    },
    openGraph: {
      siteName: "Tiber Tutor",
      locale: "en_GB",
      type: "website",
      title: formattedTitle,
      description,
      url,
    },
    twitter: {
      title: formattedTitle,
      description,
      card: "summary_large_image",
    },
  };
}

function capitaliseEachWord(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getNoIndexMetaComponents(
  page:
    | "preview"
    | "verification"
    | "password reset"
    | "forgot password"
    | "admin-tools"
    | "performance"
    | "completion"
    | "dashboard"
    | "account"
    | "membership"
    | "test"
    | "maintenance"
    | "not found",
) {
  let title;
  let description;
  switch (page) {
    case "preview":
      title = `Preview Resources » Tiber Tutor | Sail through the IB`;
      description = "A preview content page for our valued content creators!";
      break;
    case "admin-tools":
      title = `Admin Tools » Tiber Tutor | Sail through the IB`;
      description =
        "Access internal Tiber Tutor admin tools for managing subjects, resources, users, and platform settings.";
      break;
    case "verification":
      title = `Verify Your Email » Tiber Tutor | Sail through the IB`;
      description =
        "Confirm your email address to activate your Tiber Tutor account and unlock IB resources tailored to you.";
      break;
    case "password reset":
      title = `Reset Password » Tiber Tutor | Sail through the IB`;
      description =
        "Securely reset your Tiber Tutor password so you can get back to your IB revision and exam prep.";
      break;
    case "forgot password":
      title = `Forgot Password » Tiber Tutor | Sail through the IB`;
      description =
        "Request a secure password reset link for your Tiber Tutor account and quickly regain access to your IB resources.";
      break;
    case "performance":
      title = `Performance Insights » Tiber Tutor | Sail through the IB`;
      description =
        "Track your IB performance across topics, tests, and subjects to see where you’re strong and what to revise next.";
      break;
    case "completion":
      title = `Completion Summary » Tiber Tutor | Sail through the IB`;
      description =
        "View your completion summary for this activity, including what you’ve finished and suggested next steps.";
      break;
    case "account":
      title = `Account Settings » Tiber Tutor | Sail through the IB`;
      description =
        "Manage your Tiber Tutor account details, email preferences, and security settings in one place.";
      break;
    case "membership":
      title = `Membership & Billing » Tiber Tutor | Sail through the IB`;
      description =
        "Review and manage your Tiber Tutor membership, billing details, and subscription options for IB resources.";
      break;
    case "dashboard":
      title = `Your IB Dashboard » Tiber Tutor | Sail through the IB`;
      description =
        "See your IB subjects, recent activity, and recommended next steps to keep your revision on track.";
      break;
    case "test":
      title = `Take a Test » Tiber Tutor | Sail through the IB`;
      description =
        "Sit an IB-style test built from Tiber Tutor questions to check your understanding and exam readiness.";
      break;
    case "maintenance":
      title = `Site Maintenance » Tiber Tutor | Sail through the IB`;
      description =
        "We're currently performing essential maintenance to improve your experience. We'll be back online within the next 24 hours.";
      break;
    case "not found":
      title = `Page Not Found » Tiber Tutor | Sail through the IB`;
      description =
        "The page you are looking for does not exist. Please check the URL and try again.";
      break;
  }
  return {
    title,
    description,
    robots: {
      index: false,
      follow: false,
    },
  };
}

/** Hierarchical SEO: one primary keyword + level-specific secondaries so pages don't compete. */
function getResourceLevelKeywords(
  resourcePage: OpenGraphMetaProps["page"],
  subjectName: string,
  shortSubjectName: string,
  topicMeta: MainMetaMapTopic | undefined,
  subTopicMeta: MainMetaMapSubTopic | undefined,
) {
  let primaryKeyword: string = "";
  let secondaryKeywords: string[] = [];
  // subject level directory page
  if (!topicMeta) {
    primaryKeyword = `IB ${subjectName} ${resourcePage}`;
    secondaryKeywords = [
      `Revision ${resourcePage} IB ${subjectName}`,
      `HL IB ${shortSubjectName} ${resourcePage}`,
      `SL IB ${shortSubjectName} ${resourcePage}`,
    ];
  }
  // topic level directory page
  else if (topicMeta && !subTopicMeta) {
    primaryKeyword = `IB ${subjectName} Topic ${topicMeta.number} ${resourcePage}`;
    secondaryKeywords = [
      `IB ${subjectName} ${topicMeta.title} ${resourcePage}`,
      `${topicMeta.isHL ? "HL" : "SL"} IB ${shortSubjectName} ${
        topicMeta.title
      } ${resourcePage}`,
    ];
  }
  // subtopic level directory page
  else if (topicMeta && subTopicMeta) {
    primaryKeyword = `IB ${subjectName} ${subTopicMeta.label} ${
      subTopicMeta.isHL ? "HL" : "SL"
    } ${resourcePage}`;
    secondaryKeywords = [
      `IB ${subjectName} ${subTopicMeta.title} ${resourcePage}`,
      `${
        subTopicMeta.isHL ? "HL" : "SL"
      } IB ${shortSubjectName} ${resourcePage} ${subTopicMeta.title}`,
    ];
  }
  return [primaryKeyword, ...secondaryKeywords];
}

function getResourceLevelUrl(
  resourcePage: OpenGraphMetaProps["page"],
  params: OpenGraphIbParams,
) {
  return `/ib/${params.subject}/${titleToUrl(resourcePage)}${
    params.topic ? `/${params.topic}` : ""
  }${params.subTopic ? `/${params.subTopic}` : ""}`;
}

function getResourceLevelTitle(
  resourcePage: OpenGraphMetaProps["page"],
  subjectName: string,
  topicNumber: string | undefined,
  subTopicNumber?: string | undefined,
) {
  return `IB ${subjectName} ${
    subTopicNumber
      ? `${subTopicNumber} `
      : topicNumber
        ? `Topic ${topicNumber} `
        : ""
  }${resourcePage}`;
}

function getResourceLevelDescription(
  resourcePage: OpenGraphMetaProps["page"],
  subjectName: string,
  shortSubjectName: string,
  topicMeta: MainMetaMapTopic | undefined,
  subTopicMeta: MainMetaMapSubTopic | undefined,
) {
  // Pages can have either an subject level directory page (entry 1) ** NOT ALL DO
  // or a topic level directory page (entry 2) ** NOT ALL DO
  // or a subtopic level directory page (entry 3) ** NOT ALL DO
  let description: string = "";
  switch (resourcePage) {
    // *************** those just with topic (subject or topic level)
    case "videos":
      if (!topicMeta) {
        description = `Animated IB ${subjectName} videos. Watch exam-focused video lessons to build understanding before attempting IB ${shortSubjectName} exam questions and papers.`;
      } else {
        description = `Animated IB ${subjectName} videos for topic ${topicMeta.number}. Watch exam-focused video lessons to build understanding before attempting IB ${shortSubjectName} exam questions and papers.`;
      }
      break;
    case "cram sheets":
      if (!topicMeta) {
        description = `One-page IB ${subjectName} cram sheets. Summarise key formulas and ideas for fast last-minute IB ${shortSubjectName} revision.`;
      } else {
        description = `One-page IB ${subjectName} cram sheet for topic ${topicMeta.number}, summarising key formulas and ideas for fast last-minute IB ${shortSubjectName} revision.`;
      }
      break;
    case "topic tests":
      if (!topicMeta) {
        description = `Pre-built IB ${subjectName} topic tests. Exam-style questions with mark schemes to prepare for the IB ${shortSubjectName} papers.`;
      } else {
        description = `Pre-built IB ${subjectName} topic tests for topic ${topicMeta.number}, using exam-style questions with mark schemes. Sit them under timed conditions to prepare for the IB ${shortSubjectName} papers.`;
      }
      break;
    // *************** those with subject, topic and subtopic levels
    case "definitions":
      if (!topicMeta) {
        description = `Key IB ${subjectName} definitions. Master the exact wording examiners expect in long-answer questions.`;
      } else if (topicMeta && !subTopicMeta) {
        description = `Key IB ${subjectName} definitions for topic ${topicMeta.number}. Master the exact wording examiners expect in long-answer questions.`;
      } else if (topicMeta && subTopicMeta) {
        description = `Key IB ${subjectName} definitions for ${subTopicMeta.title}. Master the exact wording examiners expect in long-answer questions for this sub-topic.`;
      }
      break;
    case "flashcards":
      if (!topicMeta) {
        description = `Flashcard decks for IB ${subjectName}. Memorise definitions, formulas and core concepts before attempting exam questions.`;
      } else if (topicMeta && !subTopicMeta) {
        description = `Flashcard decks for IB ${subjectName} topic ${topicMeta.number}, helping you memorise definitions, formulas and core concepts before attempting exam questions.`;
      } else if (topicMeta && subTopicMeta) {
        description = `Flashcard decks for IB ${subjectName} ${subTopicMeta.title}, designed to lock in definitions and formulas for the IB ${shortSubjectName} exams.`;
      }
      break;
    case "notes":
      if (!topicMeta) {
        description = `IB ${subjectName} revision notes with diagrams and worked examples, written to match the IB mark schemes.`;
      } else if (topicMeta && !subTopicMeta) {
        description = `IB ${subjectName} revision notes for topic ${topicMeta.number} with diagrams and worked examples, written to match the IB mark schemes.`;
      } else if (topicMeta && subTopicMeta) {
        description = `IB ${subjectName} revision notes for ${subTopicMeta.title} with diagrams and worked examples, written to match the IB mark schemes.`;
      }
      break;
    case "questions":
      if (!topicMeta) {
        description = `Exam-style IB ${subjectName} questions with full mark schemes and worked solutions, ideal past-paper practice for the IB ${shortSubjectName} exams.`;
      } else if (topicMeta && !subTopicMeta) {
        description = `Exam-style IB ${subjectName} questions for topic ${topicMeta.number} with full mark schemes and worked solutions, ideal past-paper practice for the IB ${shortSubjectName} exams.`;
      } else if (topicMeta && subTopicMeta) {
        description = `Exam-style IB ${subjectName} questions for ${subTopicMeta.title} with full mark schemes and worked solutions, ideal past-paper practice for the IB ${shortSubjectName} exams.`;
      }
      break;
  }
  return description;
}

function getShorthandSubject(subjectName: string) {
  return subjectName.toLowerCase() === "biology"
    ? "Bio"
    : subjectName.slice(0, 4);
}

function getTopicAndSubTopicMetaFromParams(
  mainMetaMap: MainMetaMap,
  params: OpenGraphIbParams | undefined,
) {
  if (!params) return { topicMeta: undefined, subTopicMeta: undefined };
  function standardiseSubTopicTitle(title: string) {
    return titleToUrl(title.trim().toLowerCase());
  }
  let topicMeta: MainMetaMapTopic | undefined;
  let subTopicMeta: MainMetaMapSubTopic | undefined;
  // get topic meta
  if (params.topic) {
    topicMeta = (
      Object.values(mainMetaMap) as MainMetaMap[keyof MainMetaMap][]
    ).find((meta) =>
      params.topic?.endsWith(titleToUrl(meta.title)),
    ) as MainMetaMapTopic;
  }
  // get subtopic meta
  if (params.subTopic) {
    subTopicMeta = (
      Object.values(mainMetaMap) as MainMetaMap[keyof MainMetaMap][]
    ).find(
      (meta) =>
        standardiseSubTopicTitle(meta.title) ===
        standardiseSubTopicTitle(params.subTopic ?? ""),
    ) as MainMetaMapSubTopic;
  }
  return { topicMeta, subTopicMeta };
}

/** Params for `/ib/...` metadata routes (blog pages omit `params`). */
export type OpenGraphIbParams = {
  subject: string;
  topic?: string;
  subTopic?: string;
  testID?: undefined;
};

export type OpenGraphMetaProps =
  | {
      page: "definitions" | "flashcards" | "notes" | "questions";
      params: {
        subject: string;
        topic?: string;
        subTopic?: string;
        testID?: undefined;
      };
    }
  | {
      page: "cram sheets" | "videos" | "topic tests";
      params: {
        subject: string;
        topic?: string;
        subTopic?: undefined;
        testID?: undefined;
      };
    }
  | {
      page:
        | "overall subject"
        | "command terms"
        | "exam breakdown"
        | "ia examples"
        | "ia criteria"
        | "syllabus"
        | "test taker"
        | "test builder"
        | "exam tests"
        | "mock exams";
      params: {
        subject: string;
        topic?: undefined;
        subTopic?: undefined;
        testID?: undefined;
      };
    }
  | {
      page:
        | "login"
        | "register"
        | "home"
        | "team"
        | "pricing"
        // | "results"
        | "about"
        | "parents"
        | "students"
        | "terms"
        | "privacy"
        | "blog";
      params?: undefined;
    }
  | {
      page: "blog article";
      blogArticle: {
        title: string;
        slug: string;
        metaDescription: string | null;
        keywords: string[];
      };
    };
